/**
 * IncidentsModule - Incident reporting and management system
 * Follows DefectsModule pattern exactly: 2 tabs, 2/3 + 1/3 layout, sidebar
 *
 * REWORK V2 (2026-02-25):
 * - Reduced from 4 tabs to 2 tabs (Incidents, History)
 * - Added 2/3 + 1/3 layout with IncidentsSidebar
 * - All incidents are editable (investigation, corrective actions)
 * - Uses useIncidentsData hook for Azure sync
 */

import React, { useState, useMemo, useCallback } from 'react';
import { AlertTriangle, Plus, PlusCircle, Search, Clock, Download } from 'lucide-react';
import TabNavigation from '../../TabNavigation';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import IncidentsSidebar from './IncidentsSidebar';
import IncidentsSummaryCard from './IncidentsSummaryCard';
import IncidentsSettingsView from './IncidentsSettingsView';
import IncidentsHistoryView from './IncidentsHistoryView';
import IncidentReportView from './IncidentReportView';
import IncidentEditView from './IncidentEditView';
import { useIncidentsData } from '../../../../hooks/domain/useIncidentsData';
import { useVessel } from '../../../../contexts/VesselContext';
import { useCustomDialogs } from '../../../../hooks/useCustomDialogs';
import { useMainLogbook } from '../../../../hooks/domain/operations/useMainLogbook';
import FilterTabs from '../../common/FilterTabs';
import Dropdown from '../../../common/Dropdown';
// FT-357: shell-level back handler — register a "back to dashboard" handler
// so mobile users always see a Back button in the bottom bar.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

const IncidentsModule = ({ setActiveModule }) => {
  // FT-357: register a shell-level "back to dashboard" handler so mobile
  // users always see a Back button in the bottom bar for this module.
  useSetBackHandler(
    useCallback(() => {
      if (setActiveModule) setActiveModule('dashboard');
    }, [setActiveModule])
  );

  // Tab and view state
  const [activeTab, setActiveTab] = useState('incidents');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'open', 'investigating', 'actions_pending'
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');

  // Full-screen view state
  const [showReportView, setShowReportView] = useState(false);
  const [showEditView, setShowEditView] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);

  // Hooks
  const { currentVesselId } = useVessel();
  const { showAlert, AlertDialog, ConfirmDialog } = useCustomDialogs();
  const { logIncident } = useMainLogbook();

  // Use domain hook for incidents data
  const {
    data: incidents,
    save: saveIncident,
    getStats,
    quickCloseIncident
  } = useIncidentsData();

  // Tab configuration - 2 tabs only (matching Defects)
  const tabs = [
    { id: 'incidents', label: 'Incidents' },
    { id: 'history', label: 'Incident History' }
  ];

  // Calculate stats for sidebar
  const stats = useMemo(() => {
    return getStats();
  }, [incidents, getStats]);

  // Filter incidents
  const filteredIncidents = useMemo(() => {
    let filtered = incidents || [];

    // Exclude closed for main view (closed go to history)
    filtered = filtered.filter(i => i.status !== 'closed');

    // Apply status filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(i => i.status === activeFilter);
    }

    // Apply severity filter
    if (severityFilter !== 'all') {
      filtered = filtered.filter(i => i.severity === severityFilter);
    }

    // Apply search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(i =>
        i.description?.toLowerCase().includes(term) ||
        i.location?.toLowerCase().includes(term) ||
        i.id?.toString().toLowerCase().includes(term)
      );
    }

    // Sort by date (newest first), then by severity
    return filtered.sort((a, b) => {
      // Critical severity first
      if (a.severity === 'critical' && b.severity !== 'critical') return -1;
      if (b.severity === 'critical' && a.severity !== 'critical') return 1;
      // Then by date
      return new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt);
    });
  }, [incidents, activeFilter, severityFilter, searchTerm]);

  // Handle new incident report
  const handleReportIncident = useCallback(async (incidentData) => {
    try {
      const newIncident = {
        ...incidentData,
        status: 'open',
        investigationStatus: 'pending',
        vesselId: currentVesselId,
        createdAt: new Date().toISOString()
      };

      const savedIncident = saveIncident(newIncident);

      // Log to Official Logbook
      await logIncident({
        incidentId: savedIncident.id,
        type: savedIncident.type,
        description: savedIncident.description,
        severity: savedIncident.severity,
        location: savedIncident.location,
        reportedBy: savedIncident.reportedBy,
        timestamp: savedIncident.createdAt
      });

      setShowReportView(false);
      showAlert('Incident Reported', `Incident ${savedIncident.id} has been successfully reported.`, 'success');
    } catch (error) {
      showAlert('Error', 'Failed to save incident', 'error');
    }
  }, [saveIncident, currentVesselId, logIncident, showAlert]);

  // Handle save incident (from edit view)
  const handleSaveIncident = useCallback(async (incidentData) => {
    try {
      saveIncident(incidentData);
      setShowEditView(false);
      setSelectedIncident(null);
    } catch (error) {
      showAlert('Error', 'Failed to save changes', 'error');
    }
  }, [saveIncident, showAlert]);

  // Handle quick close
  const handleQuickClose = useCallback(async (incidentId, { rootCause, resolution }) => {
    try {
      quickCloseIncident(incidentId, { rootCause, resolution });
      setShowEditView(false);
      setSelectedIncident(null);
      showAlert('Incident Closed', 'The incident has been quick closed.', 'success');
    } catch (error) {
      showAlert('Error', 'Failed to close incident', 'error');
    }
  }, [quickCloseIncident, showAlert]);

  // Handle sidebar quick actions
  const handleQuickAction = useCallback((action) => {
    switch (action) {
      case 'report':
        setShowReportView(true);
        break;
      case 'history':
        setActiveTab('history');
        break;
      case 'settings':
        setShowSettings(true);
        break;
      default:
        break;
    }
  }, []);

  // Handle incident card click
  const handleViewIncident = useCallback((incident) => {
    setSelectedIncident(incident);
    setShowEditView(true);
  }, []);

  // Helper functions
  const getSeverityColor = (severity) => {
    const colors = {
      'critical': { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
      'high': { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
      'medium': { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
      'low': { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' }
    };
    return colors[severity?.toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-500' };
  };

  const getStatusBadge = (status) => {
    const badges = {
      'open': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Open' },
      'investigating': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Investigating' },
      'actions_pending': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Actions Pending' }
    };
    return badges[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
  };

  const getTypeLabel = (type) => {
    const types = {
      'accident': 'Accident',
      'near_miss': 'Near Miss',
      'hazard': 'Hazard',
      'environmental': 'Environmental',
      'equipment_failure': 'Equipment Failure',
      'personnel_injury': 'Personnel Injury',
      'safety': 'Safety'
    };
    return types[type] || type || 'Unknown';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Render incident card
  const renderIncidentCard = (incident) => {
    const sevColors = getSeverityColor(incident.severity);
    const statusBadge = getStatusBadge(incident.status);

    return (
      <button
        key={incident.id}
        onClick={() => handleViewIncident(incident)}
        className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-cyan-300 hover:shadow-sm transition-all"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sevColors.bg} ${sevColors.text}`}>
                {incident.severity?.charAt(0).toUpperCase() + incident.severity?.slice(1)}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                {statusBadge.label}
              </span>
            </div>
            <p className="font-medium text-gray-900 line-clamp-2">
              {incident.description || 'No description'}
            </p>
            <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
              <span>{formatDate(incident.date)}</span>
              <span>{getTypeLabel(incident.type)}</span>
              {incident.location && <span>{incident.location}</span>}
            </div>
          </div>
          <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${sevColors.text}`} />
        </div>
      </button>
    );
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'incidents':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content - 2/3 width */}
            <div className="lg:col-span-2 pb-20 lg:pb-0">
              <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
                {/* Main Header with Search */}
                <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
                  <div className="flex items-center justify-between gap-6">
                    <h3 className="text-base font-semibold text-white whitespace-nowrap flex-shrink-0">
                      Active Incidents
                    </h3>

                    {/* Search Input */}
                    <div className="relative flex-1 max-w-2xl">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search incidents..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-white text-slate-800 text-sm border border-white/20 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Filter Section */}
                <div className="px-6 py-5 space-y-3">
                  {/* Row 1: Severity Filter */}
                  <div className="flex gap-3">
                    <Dropdown
                      value={severityFilter}
                      onChange={setSeverityFilter}
                      width="flex-1 sm:flex-none sm:w-44"
                      options={[
                        { value: 'all', label: 'All Severity' },
                        { value: 'critical', label: 'Critical' },
                        { value: 'high', label: 'High' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'low', label: 'Low' }
                      ]}
                    />
                  </div>

                  {/* Row 2: Status Filter Tabs */}
                  <div className="flex items-center justify-between">
                    <FilterTabs
                      tabs={[
                        { id: 'all', label: 'All', count: stats.open + stats.investigating + stats.actionsPending },
                        { id: 'open', label: 'Open', count: stats.open },
                        { id: 'investigating', label: 'Investigating', count: stats.investigating },
                        { id: 'actions_pending', label: 'Actions Pending', count: stats.actionsPending }
                      ]}
                      activeTab={activeFilter}
                      onTabChange={setActiveFilter}
                    />
                  </div>
                </div>

                {/* Content area */}
                <div className="p-6">
                  {filteredIncidents.length === 0 ? (
                    <div className="text-center py-12">
                      <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No incidents</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {searchTerm || severityFilter !== 'all' || activeFilter !== 'all'
                          ? 'Try adjusting your filters'
                          : 'No active incidents to display'}
                      </p>
                      <button
                        onClick={() => setShowReportView(true)}
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
                      >
                        <Plus className="h-4 w-4" />
                        Report Incident
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredIncidents.map(incident => renderIncidentCard(incident))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar - 1/3 width - Hidden on mobile */}
            <div className="hidden lg:block">
              <IncidentsSidebar
                stats={stats}
                onQuickAction={handleQuickAction}
              />
            </div>
          </div>
        );

      case 'history':
        return (
          <IncidentsHistoryView
            onBack={() => setActiveTab('incidents')}
          />
        );

      default:
        return null;
    }
  };

  // FT-358: register bottom-bar middle-slot actions per brief §6.
  // Sub-views get []; history tab gets just Export; list gets 3 actions.
  const exportIncidents = () =>
    showAlert('Export', 'Incident export coming soon.', 'info');
  useSetBottomBarActions(
    showSettings || showReportView || showEditView
      ? []
      : activeTab === 'history'
      ? [{ key: 'incidents-history-export', icon: Download, label: 'Export', onClick: exportIncidents }]
      : [
          {
            key: 'incidents-report', icon: PlusCircle, label: 'Report',
            primary: true, onClick: () => setShowReportView(true),
          },
          { key: 'incidents-history', icon: Clock, label: 'History', onClick: () => setActiveTab('history') },
          { key: 'incidents-export', icon: Download, label: 'Export', onClick: exportIncidents },
        ],
  );

  // Full-screen views
  if (showSettings) {
    return (
      <IncidentsSettingsView
        onBack={() => setShowSettings(false)}
      />
    );
  }

  if (showReportView) {
    return (
      <IncidentReportView
        onBack={() => setShowReportView(false)}
        onSave={handleReportIncident}
      />
    );
  }

  if (showEditView && selectedIncident) {
    return (
      <IncidentEditView
        incident={selectedIncident}
        onBack={() => {
          setShowEditView(false);
          setSelectedIncident(null);
        }}
        onSave={handleSaveIncident}
        onQuickClose={handleQuickClose}
      />
    );
  }

  return (
    <div>
      {/* Module Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Incidents</h1>
        <p className="mt-1 text-sm text-gray-500">
          Report, investigate, and manage incidents with corrective actions
        </p>
      </div>

      {/* Tab Navigation */}
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      <div className="mt-6">
        {renderTabContent()}
      </div>

      {/* Mobile Summary - shown below content on mobile */}
      {activeTab === 'incidents' && (
        <div className="lg:hidden mb-20 px-4 mt-6">
          <IncidentsSummaryCard stats={stats} />
        </div>
      )}

      {/* FT-358: mobile bottom-bar actions registered via
          useSetBottomBarActions above — the shell owns the bar. */}

      {/* Dialogs */}
      <AlertDialog />
      <ConfirmDialog />
    </div>
  );
};

export default IncidentsModule;
